import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Generate a secure quote token with 'qt_' prefix.
 * Uses crypto.randomUUID() for security.
 *
 * @example
 * generateQuoteToken() // "qt_a1b2c3d4-e5f6-7890-abcd-ef1234567890"
 */
export function generateQuoteToken(): string {
  return `qt_${crypto.randomUUID()}`
}

/**
 * Generate a secure operator pricing token with 'qo_' prefix.
 * Used for the operator set-price page on operator-priced (quote-only)
 * quotes. Kept separate from the customer token so the customer link
 * never grants pricing rights.
 *
 * @example
 * generateOperatorQuoteToken() // "qo_a1b2c3d4-e5f6-7890-abcd-ef1234567890"
 */
export function generateOperatorQuoteToken(): string {
  return `qo_${crypto.randomUUID()}`
}

/**
 * Generate a secure balance-payment token with 'bp_' prefix.
 *
 * Authorizes paying off the outstanding balance on ONE booking, and nothing
 * else — it grants no access to the quote, the customer record, or any other
 * booking. Kept distinct from `qt_`/`qo_` so a link's prefix says exactly what
 * it can do.
 *
 * @example
 * generateBalancePaymentToken() // "bp_a1b2c3d4-e5f6-7890-abcd-ef1234567890"
 */
export function generateBalancePaymentToken(): string {
  return `bp_${crypto.randomUUID()}`
}

/**
 * Format a price for display
 */
export function formatPrice(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

/**
 * Format a date for display.
 * Optionally accepts a timezone to display in a specific timezone.
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
  timezone?: string
): string {
  const opts: Intl.DateTimeFormatOptions = { ...options }
  if (timezone) {
    opts.timeZone = timezone
  }
  return new Intl.DateTimeFormat('en-US', opts).format(
    typeof date === 'string' ? new Date(date) : date
  )
}

/**
 * Format a UTC datetime in a specific timezone.
 */
export function formatDateTimeInTimezone(
  date: string | Date,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const defaults: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    ...options,
  }
  return new Intl.DateTimeFormat('en-US', defaults).format(
    typeof date === 'string' ? new Date(date) : date
  )
}

/**
 * Construct a UTC Date from a date string and time string
 * interpreted in a specific timezone.
 *
 * For example, dateInTimezone('2024-06-15', '14:00', 'America/Los_Angeles')
 * returns a Date whose UTC value corresponds to 2:00 PM Pacific time on June 15.
 */
export function dateInTimezone(dateStr: string, timeStr: string, timezone: string): Date {
  // Parse the date and time components
  const [year, month, day] = dateStr.split('-').map(Number)
  const [hours, minutes] = timeStr.split(':').map(Number)

  // Create a date in UTC with the same wall-clock values
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hours, minutes))

  // Use Intl to find what the offset is for the target timezone at this moment
  // by formatting the UTC guess in the target timezone and comparing
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const parts = formatter.formatToParts(utcGuess)
  const get = (type: string) => {
    const part = parts.find(p => p.type === type)
    return part ? parseInt(part.value, 10) : 0
  }

  const tzYear = get('year')
  const tzMonth = get('month')
  const tzDay = get('day')
  const tzHour = get('hour') === 24 ? 0 : get('hour')
  const tzMinute = get('minute')

  // The UTC guess formatted in the target TZ gives us what the TZ thinks
  // the time is. The difference between what we want and what TZ shows
  // is the adjustment we need.
  const tzDate = new Date(Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute))
  const offsetMs = utcGuess.getTime() - tzDate.getTime()

  // Apply the offset: we want the wall clock to show our desired time
  const result = new Date(utcGuess.getTime() + offsetMs)

  // Verify by formatting again (handles DST edge cases where the offset
  // might differ between our guess and the corrected time)
  const verifyParts = formatter.formatToParts(result)
  const vGet = (type: string) => {
    const part = verifyParts.find(p => p.type === type)
    return part ? parseInt(part.value, 10) : 0
  }
  const vHour = vGet('hour') === 24 ? 0 : vGet('hour')
  const vMinute = vGet('minute')

  if (vHour !== hours || vMinute !== minutes) {
    // DST transition caused a shift, recalculate
    const vDate = new Date(Date.UTC(vGet('year'), vGet('month') - 1, vGet('day'), vHour, vMinute))
    const newOffsetMs = result.getTime() - vDate.getTime()
    return new Date(utcGuess.getTime() + newOffsetMs)
  }

  return result
}

/**
 * Format a raw pickup date/time for display without browser timezone conversion.
 *
 * Unlike `new Date('YYYY-MM-DDTHH:MM')` which parses in the browser's local timezone,
 * this function formats the time directly from the raw strings so the displayed time
 * always matches what the customer picked, regardless of their browser timezone.
 *
 * @example
 * formatPickupDateTime('2024-06-15', '14:00', 'America/Los_Angeles')
 * // "Sat, Jun 15, 2024, 2:00 PM (PDT)"
 */
export function formatPickupDateTime(dateStr: string, timeStr: string, timezone: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number)

  // Use dateInTimezone to get the correct UTC moment for this wall-clock time.
  // We only use this for the date portion (day-of-week, month name) since
  // those are timezone-invariant for the same wall-clock date.
  const utcMoment = dateInTimezone(dateStr, timeStr, timezone)

  const datePart = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  }).format(utcMoment)

  // Format time directly from raw values (no browser TZ influence)
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHour = hours % 12 || 12
  const displayMinutes = minutes.toString().padStart(2, '0')
  const timePart = `${displayHour}:${displayMinutes} ${period}`

  // Get timezone abbreviation (e.g., "PST", "EDT")
  const tzAbbr = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  }).formatToParts(utcMoment).find(p => p.type === 'timeZoneName')?.value || timezone

  return `${datePart}, ${timePart} (${tzAbbr})`
}

/**
 * Format a phone number as (555) 555-1234
 * Accepts various input formats and normalizes them
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-numeric characters
  const digits = value.replace(/\D/g, '')

  // Limit to 10 digits (US phone number)
  const limitedDigits = digits.slice(0, 10)

  // Format based on length
  if (limitedDigits.length === 0) {
    return ''
  } else if (limitedDigits.length <= 3) {
    return `(${limitedDigits}`
  } else if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  } else {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
  }
}

/**
 * Extract raw digits from a formatted phone number
 */
export function unformatPhoneNumber(value: string): string {
  return value.replace(/\D/g, '').slice(0, 10)
}

/**
 * Validate that a phone number is a plausible NANP (North American Numbering
 * Plan) number — US, Canada, and other +1 territories.
 *
 * Rules:
 *   - Exactly 10 digits after stripping non-digits and an optional leading "1"
 *   - Area code first digit 2-9
 *   - Exchange (central office code) first digit 2-9
 *
 * Returns false for empty/partial input so it can gate submission directly.
 */
export function isValidNanpPhone(value: string | null | undefined): boolean {
  if (!value) return false

  let digits = value.replace(/\D/g, '')

  // Strip the optional country code for +1 / 1-prefixed numbers.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }

  if (digits.length !== 10) return false

  const areaCodeFirst = digits.charCodeAt(0)
  const exchangeFirst = digits.charCodeAt(3)

  // '2'..'9' → 50..57
  const isValidLead = (code: number) => code >= 50 && code <= 57
  return isValidLead(areaCodeFirst) && isValidLead(exchangeFirst)
}

/**
 * Format a string for currency input display (without currency symbol).
 * Accepts raw user input (digits, decimals, commas) and returns a formatted string.
 *
 * @example
 * formatCurrencyInput("1234.56") // "1,234.56"
 * formatCurrencyInput("1234567") // "1,234,567"
 * formatCurrencyInput("12.999") // "12.99" (truncates to 2 decimals)
 * formatCurrencyInput("") // ""
 */
export function formatCurrencyInput(value: string): string {
  if (!value || value.trim() === '') {
    return ''
  }

  // Remove everything except digits and decimal point
  let cleaned = value.replace(/[^\d.]/g, '')

  // Handle multiple decimal points - keep only the first one
  const parts = cleaned.split('.')
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('')
  }

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = cleaned.split('.')

  // Remove leading zeros from integer part (but keep at least one digit)
  const cleanedInteger = integerPart.replace(/^0+/, '') || '0'

  // Format integer part with thousand separators
  const formattedInteger = cleanedInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // If there's a decimal part, limit to 2 decimal places
  if (decimalPart !== undefined) {
    const limitedDecimal = decimalPart.slice(0, 2)
    return `${formattedInteger}.${limitedDecimal}`
  }

  return formattedInteger
}

/**
 * Parse a formatted currency string to a number.
 * Strips currency symbols, commas, and whitespace.
 *
 * @example
 * parseCurrencyValue("$1,234.56") // 1234.56
 * parseCurrencyValue("1,234.56") // 1234.56
 * parseCurrencyValue("") // 0
 * parseCurrencyValue("invalid") // 0
 */
export function parseCurrencyValue(value: string): number {
  if (!value || value.trim() === '') {
    return 0
  }

  // Remove everything except digits and decimal point
  const cleaned = value.replace(/[^\d.]/g, '')

  // Handle multiple decimal points - keep only the first one
  const parts = cleaned.split('.')
  let normalizedValue: string
  if (parts.length > 2) {
    normalizedValue = parts[0] + '.' + parts.slice(1).join('')
  } else {
    normalizedValue = cleaned
  }

  const parsed = parseFloat(normalizedValue)

  if (isNaN(parsed)) {
    return 0
  }

  // Round to 2 decimal places to avoid floating point issues
  return Math.round(parsed * 100) / 100
}

/**
 * Parse a full name into first and last name components.
 * Single word names result in lastName being null.
 * Multi-word names use the first word as firstName and the rest as lastName.
 *
 * @example
 * parseCustomerName("John") // { firstName: "John", lastName: null }
 * parseCustomerName("John Doe") // { firstName: "John", lastName: "Doe" }
 * parseCustomerName("John Van Der Berg") // { firstName: "John", lastName: "Van Der Berg" }
 * parseCustomerName("  John   Doe  ") // { firstName: "John", lastName: "Doe" }
 */
export function parseCustomerName(fullName: string): { firstName: string; lastName: string | null } {
  const trimmed = fullName.trim()

  if (!trimmed) {
    return { firstName: '', lastName: null }
  }

  // Split by whitespace (handles multiple spaces)
  const parts = trimmed.split(/\s+/)

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: null }
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  }
}
